<template>
    <DpApplicationContainer
        v-if="isAuth"
        :menuItems="menuItems">
        <template v-if="envStore.globalLoading == false">
            <DpApplicationRemindersList :modelValue="reminders" />
            <router-view v-if="companyStore.hasSelectedCompany" />
        </template>
    </DpApplicationContainer>
</template>

<script setup>
import { ref, onBeforeMount, defineComponent, defineAsyncComponent, computed, h } from "vue";
import { useAuthStore, useCompanyStore, useEnvStore } from "@dutypay/front-end-design-system";
import { menuItems } from "@front-end-application/menuItems.js";
import { interceptor } from "@dutypay/front-end-design-system";
import { to } from "await-to-js";
import { isEmpty, fromPairs, compact, map } from "lodash";

const authStore = useAuthStore();
const envStore = useEnvStore();

const isAuth = computed(() => {
    return authStore.status === "success";
});
const reminders = ref([]);
const companyStore = useCompanyStore();

async function remoteFetchReminder() {
    if (isEmpty(companyStore.selectedCompany)) return;
    let err, remoteFetch;
    [err, remoteFetch] = await to(
        interceptor.get(
            `${envStore.apiUrls.dutypay}/api/v0/reminder-data/${companyStore.selectedCompany.hs.company.id}`,
        ),
    );
    if (err) {
        return;
    }
    reminders.value = remoteFetch.data.result;
}

const DpApplicationRemindersList = defineComponent({
    props: {
        modelValue: { type: Array, required: true },
    },
    setup(props) {
        const loadComponent = (componentName) => {
            return defineAsyncComponent(() =>
                import(/* @vite-ignore */ `@dutypay/front-end-sections/src/reminders/${componentName}.vue`).catch(
                    (error) => {
                        if (envStore.debug) throw error;
                        return null;
                    },
                ),
            );
        };

        function renderReminder() {
            let renderFunctions = [];

            if (isEmpty(companyStore.selectedCompany)) renderFunctions.push(h(loadComponent("CompanySelector"), {}));

            for (const reminder of props.modelValue) {
                const ReminderComponent = loadComponent(reminder._reminder.type);
                if (ReminderComponent) {
                    renderFunctions.push(h(ReminderComponent, { modelValue: reminder }));
                }
            }
            return renderFunctions;
        }

        return () => h("div", null, renderReminder());
    },
});

const getPreSelectedCompany = () => {
    let preSelectedCompany = undefined;
    let urlHash = window.location.hash;
    let urlParameters = fromPairs(
        compact(
            map(urlHash.slice(1).split("&"), function (item) {
                if (item) return item.split("=");
            }),
        ),
    );

    if (urlParameters["company-id"]) {
        preSelectedCompany = { hs: { company: { id: decodeURIComponent(urlParameters["company-id"]) } } };
    } else {
        try {
            preSelectedCompany = JSON.parse(localStorage.getItem("selectedCompany"));
        } catch {
            preSelectedCompany = undefined;
        }
    }

    return preSelectedCompany;
};

async function initApp() {
    const preSelectedCompany = getPreSelectedCompany();

    envStore.setGlobalLoading();
    await envStore.fetchHosts();
    await authStore.tokenAuth();
    await companyStore.remoteFetchCompanies();
    if (preSelectedCompany) {
        await companyStore.switchCompany(preSelectedCompany);
    }

    remoteFetchReminder();
    envStore.releaseGlobalLoading();
}

onBeforeMount(async () => {
    await initApp();
});
</script>

<i18n>
{}
</i18n>
